import { Fragment } from "react";
import { fmtN, fmtP, heatCell } from "./Momentum";

export const TH = "px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";
export const ALL_STAGES = [["lead", "Lead"], ["enquiry", "Enquiry"], ["test_drive", "Test Drive"], ["booking", "Booking"], ["retail", "Retail"]];
export const colLabels = (period) => (period?.plan_label ? [period.plan_label, period.td_label, "Ach", "%"] : period?.kind === "week" ? ["Week Plan", "WTD Plan", "Ach", "%"] : ["Month Plan", "MTD Plan", "Ach", "%"]);
export const pctOf = (a, p) => (p > 0 && a != null ? Math.round((a / p) * 1000) / 10 : null);
export const sumCells = (rows, pick, f) => rows.reduce((s, r) => s + (Number(pick(r)?.[f]) || 0), 0);
// totals: each column sums, % recomputed from the totals (never averaged); a future period has no achievement
export const totalCell = (rows, pick, future) => {
  const plan = sumCells(rows, pick, "plan"), plan_td = sumCells(rows, pick, "plan_td"), ach = future ? null : sumCells(rows, pick, "ach");
  return { plan, plan_td: future ? null : plan_td, ach, pct: future ? null : pctOf(ach, plan_td), full_pct: future ? null : pctOf(ach, plan) };
};

// Frozen-right geometry (≥1024px): the four Retail cells are 72px each.
const STICKY = ["lg:sticky lg:right-[216px] lg:w-[72px] lg:min-w-[72px] lg:shadow-[-8px_0_10px_-8px_rgba(0,0,0,0.35)]", "lg:sticky lg:right-[144px] lg:w-[72px] lg:min-w-[72px]", "lg:sticky lg:right-[72px] lg:w-[72px] lg:min-w-[72px]", "lg:sticky lg:right-0 lg:w-[72px] lg:min-w-[72px]"];
const SEP = "border-l-2 border-border/80";

// Group header spanning the four columns of a stage, with a thin separator before it.
export function GroupHead({ label, sticky = false, testId }) {
  return <th className={`${TH} text-center ${SEP} ${sticky ? "lg:sticky lg:right-0 bg-card z-[1] lg:border-l-2" : ""}`} colSpan={4} data-testid={testId}>{label}</th>;
}

export function SubHead({ period, sticky = false, bg = "bg-card" }) {
  const labels = colLabels(period);
  return (
    <>
      {labels.map((l, i) => (
        <th key={l} className={`px-1.5 text-right font-medium whitespace-nowrap ${i === 0 ? `${SEP} text-muted-foreground` : ""} ${sticky ? `${STICKY[i]} ${bg}` : ""}`}>{l}</th>
      ))}
    </>
  );
}

// The four cells of one stage: full-period plan (muted) · plan-to-date · Ach (bold) · % (bold, heat-coded).
// `retail` adds the "x% of month plan" tooltip; `sticky` freezes the group on the right at ≥1024px; `fmt` overrides the number format (e.g. ₹ lakhs).
export function StageCells({ c, period, retail = false, sticky = false, bg = "bg-card", testPrefix, dense = false, fmt }) {
  const [planLbl, tdLbl] = colLabels(period);
  const f = fmt || fmtN;
  const py = dense ? "py-0.5" : "py-1";
  const cls = (i, extra = "") => `px-1.5 ${py} text-right num ${i === 0 ? SEP : ""} ${sticky ? `${STICKY[i]} ${bg}` : ""} ${extra}`;
  if (!c) {
    return <>{[0, 1, 2, 3].map((i) => <td key={i} className={cls(i, "text-muted-foreground")}>—</td>)}</>;
  }
  const future = c.ach == null;
  const tip = retail && !future && c.full_pct != null ? `${c.full_pct}% of ${planLbl.toLowerCase()} (${f(c.ach)} ÷ ${f(c.plan, 1)})` : undefined;
  return (
    <>
      <td className={cls(0, "text-muted-foreground")} title={planLbl} data-testid={testPrefix ? `${testPrefix}-plan` : undefined}>{c.plan == null ? "—" : f(c.plan, 1)}</td>
      <td className={cls(1)} title={tdLbl} data-testid={testPrefix ? `${testPrefix}-ptd` : undefined}>{c.plan_td == null ? "—" : f(c.plan_td, 1)}</td>
      <td className={cls(2, "font-bold")} data-testid={testPrefix ? `${testPrefix}-ach` : undefined}>{f(c.ach)}</td>
      <td className={cls(3, `font-bold ${future ? "text-muted-foreground" : heatCell(c.pct)}`)} title={tip} data-testid={testPrefix ? `${testPrefix}-pct` : undefined}>{fmtP(c.pct)}</td>
    </>
  );
}

export function GroupHeads({ stages, freezeRetail = false, testPrefix }) {
  return <>{stages.map(([k, l]) => <GroupHead key={k} label={l} sticky={freezeRetail && k === "retail"} testId={testPrefix ? `${testPrefix}-group-${k}` : undefined} />)}</>;
}

export function SubHeads({ stages, period, freezeRetail = false }) {
  return <>{stages.map(([k]) => <Fragment key={k}><SubHead period={period} sticky={freezeRetail && k === "retail"} /></Fragment>)}</>;
}
